import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Layers, Pencil, Plus } from "lucide-react";
import API from "../../../../services/api";
import { v2CreateComponent, v2ListComponents, v2UpdateComponent } from "../SalaryApiV2";
import { useToast } from "../../../Context/ToastContext";
import { useSetupProgress } from "../../../Context/SetupProgressContext";
import { markSalaryComponentsConfigured } from "../../../../services/markSalaryComponentsConfigured";
import ErrorAlert from "../../../../components/ui/ErrorAlert";
import SuccessScreen from "../../../../components/common/SuccessScreen";
import { classifyApiError } from "../../../../utils/normalizeApiError";
import ComponentEditorForm from "./components/ComponentEditorForm";
import {
  emptyForm,
  rowToForm,
  formIsValid,
  buildPayload,
} from "./components/salaryComponentModel";
import { appendWizardSelectedComponentIds } from "../wizard/shared";
import { consumePendingComponentFormula } from "./components/componentFormulaNav";
import "../../../../styles/salary-components-premium.css";

export default function SalaryComponentEditorPage() {
  const { componentId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};

  const isEdit = Boolean(componentId);
  const mode = isEdit ? "edit" : "create";
  const defaultCategory = searchParams.get("category") === "deduction" ? "deduction" : "earning";

  const [loading, setLoading] = useState(isEdit);
  const [errorInfo, setErrorInfo] = useState(null);
  const [initialRow, setInitialRow] = useState(location.state?.row ?? null);
  const [extraWork, setExtraWork] = useState(null);
  const [form, setForm] = useState(() => (isEdit && initialRow ? rowToForm(initialRow) : emptyForm(defaultCategory)));
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // "created" | "updated" once the API confirms the save; shows the success screen.
  const [savedAction, setSavedAction] = useState(null);

  const listPath = "/salary-v2/components";
  const appliedFormulaRef = useRef(false);

  useEffect(() => {
    if (!isEdit || !componentId || appliedFormulaRef.current) return;
    const fromState = location.state?.formulaExpression;
    const pending = consumePendingComponentFormula(componentId);
    const expr = String(fromState || pending || "").trim();
    if (!expr) return;
    appliedFormulaRef.current = true;
    setForm((f) => ({
      ...f,
      formula_expression: expr,
      calculation_type: "formula",
    }));
    toast.success("Formula applied — review and save your changes.");
    if (fromState) {
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state || {}), formulaExpression: undefined },
      });
    }
  }, [isEdit, componentId, location.pathname, location.state, navigate, toast]);

  useEffect(() => {
    let cancelled = false;
    const rowFromNav = location.state?.row ?? null;
    async function load() {
      try {
        setErrorInfo(null);
        if (!isEdit) {
          const cfgRes = await API.get("/organisation/me/extra-work-config").catch(() => null);
          if (!cancelled) setExtraWork(cfgRes?.data?.extra_work || null);
          return;
        }
        if (rowFromNav) {
          setLoading(false);
          const cfgRes = await API.get("/organisation/me/extra-work-config").catch(() => null);
          if (!cancelled) setExtraWork(cfgRes?.data?.extra_work || null);
          return;
        }
        setLoading(true);
        const [data, cfgRes] = await Promise.all([
          v2ListComponents(),
          API.get("/organisation/me/extra-work-config").catch(() => null),
        ]);
        if (cancelled) return;
        setExtraWork(cfgRes?.data?.extra_work || null);
        const list = Array.isArray(data) ? data : [];
        const row = list.find((r) => String(r.component_id) === String(componentId));
        if (!row) {
          setErrorInfo({ title: "Not found", message: "This component could not be loaded.", hint: "Return to the components list and try again." });
          return;
        }
        setInitialRow(row);
        setForm(rowToForm(row));
      } catch (e) {
        if (!cancelled) {
          const info = classifyApiError(e, "Failed to load component");
          setErrorInfo(info);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [componentId, isEdit, location.state?.row]);

  const canSave = useMemo(() => formIsValid(form), [form]);

  async function acknowledgeConfigured() {
    await markSalaryComponentsConfigured();
    await refetchSetupProgress?.();
  }

  async function handleSave() {
    setSubmitted(true);
    if (!canSave) {
      toast.error("Fill in all required fields before saving.");
      return;
    }
    const payload = buildPayload(form);
    try {
      setSaving(true);
      if (mode === "edit") {
        await v2UpdateComponent(initialRow.component_id, payload);
        await acknowledgeConfigured();
        setSavedAction("updated");
        return;
      }
      const code = String(payload?.code || "").trim().toUpperCase();
      if (code === "OVERTIME" || code === "OT" || code === "ADVANCE_TAKEN") {
        toast.error("Use the system-defined Overtime and Advance Taken components instead of creating duplicates.");
        return;
      }
      const created = await v2CreateComponent(payload);
      if (created?.component_id) {
        appendWizardSelectedComponentIds([created.component_id]);
      }
      await acknowledgeConfigured();
      setSavedAction("created");
    } catch (e) {
      const info = classifyApiError(e, mode === "edit" ? "Failed to update component" : "Failed to create component");
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  }

  const title = isEdit
    ? `Edit component — ${form.code || initialRow?.code || ""}`
    : "Add component";
  const TitleIcon = isEdit ? Pencil : Plus;

  if (savedAction) {
    return (
      <div className="scp scp-editor-page">
        <SuccessScreen
          variant="panel"
          title={`Salary component ${savedAction} successfully!`}
          message="Redirecting to salary components…"
          onComplete={() => navigate(listPath)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="scp scp-editor-page">
        <section className="scp-card scp-editor-card">
          <div className="scp-skel scp-skel-row" style={{ width: "35%" }} />
          <div className="scp-skel scp-skel-line" style={{ height: 120, marginTop: 16 }} />
        </section>
      </div>
    );
  }

  return (
    <div className="scp scp-editor-page">
      {errorInfo ? (
        <ErrorAlert
          title={errorInfo.title}
          message={errorInfo.message}
          hint={errorInfo.hint}
          onDismiss={() => navigate(listPath)}
          onRetry={() => window.location.reload()}
        />
      ) : (
        <section className="scp-card scp-editor-card">
          <header className="scp-editor-header">
            <div className="scp-editor-header-main">
              <span className="scp-modal-head-icon" aria-hidden>
                <TitleIcon size={18} />
              </span>
              <div>
                <h1 className="scp-editor-title">
                  <Layers size={20} className="scp-title-icon-inline" aria-hidden />
                  {title}
                </h1>
                <p className="scp-editor-subtitle">
                  {isEdit
                    ? "Update every setting on one page — no step-by-step wizard."
                    : "Define code, category, calculation, and status in one place."}
                </p>
              </div>
            </div>
          </header>

          <ComponentEditorForm
            form={form}
            setForm={setForm}
            mode={mode}
            initialRow={initialRow}
            componentId={isEdit ? componentId : null}
            extraWork={extraWork}
            onOpenExtraWork={() => navigate("/salary-v2/overtime")}
            submitted={submitted}
          />

          <footer className="scp-editor-footer">
            <button type="button" className="bw-btn bw-btn-secondary" disabled={saving} onClick={() => navigate(listPath)}>
              Cancel
            </button>
            <button type="button" className="bw-btn bw-btn-primary" disabled={!canSave || saving} onClick={handleSave}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create component"}
            </button>
          </footer>
        </section>
      )}
    </div>
  );
}
